/**
 * 节点状态样式映射
 *
 * 统一管理工作流节点执行状态的样式配置
 * 包括边框颜色、背景色、阴影、动画等
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NodeExecutionStatus } from '../../../types/workflow';

// ==================== 节点状态样式配置 ====================

export interface NodeStatusStyle {
  /** 边框颜色类 */
  border: string;
  /** 背景色类（用于头部） */
  bg?: string;
  /** 文字颜色类 */
  text?: string;
  /** 阴影类 */
  shadow?: string;
  /** 动画类 */
  animation?: string;
  /** 透明度 */
  opacity?: string;
  /** 额外的类 */
  extra?: string;
}

/**
 * 节点执行状态样式映射
 *
 * 定义每个执行状态对应的 CSS 类
 */
export const NODE_STATUS_STYLES: Record<NodeExecutionStatus, NodeStatusStyle> = {
  [NodeExecutionStatus.PENDING]: {
    border: 'border-gray-300',
    opacity: 'opacity-90',
  },
  [NodeExecutionStatus.RUNNING]: {
    border: 'border-paper-500',
    shadow: 'shadow-lg shadow-paper-200',
    animation: 'animate-node-pulse',
  },
  [NodeExecutionStatus.COMPLETED]: {
    border: 'border-success-500',
    bg: 'bg-success-50',
    text: 'text-success-600',
  },
  [NodeExecutionStatus.FAILED]: {
    border: 'border-error-500',
    bg: 'bg-error-50',
    text: 'text-error-600',
    shadow: 'shadow-sm shadow-error-100',
  },
  [NodeExecutionStatus.SKIPPED]: {
    border: 'border-gray-300',
    opacity: 'opacity-60',
  },
};

// ==================== 连接点状态样式 ====================

export interface HandleStatusStyle {
  /** 连接点背景色 */
  bg: string;
  /** 连接点边框色 */
  border: string;
  /** 是否显示动画 */
  animated?: boolean;
}

/**
 * 连接点执行状态样式映射
 */
export const HANDLE_STATUS_STYLES: Record<NodeExecutionStatus, HandleStatusStyle> = {
  [NodeExecutionStatus.PENDING]: {
    bg: 'bg-gray-400',
    border: 'border-gray-300',
  },
  [NodeExecutionStatus.RUNNING]: {
    bg: 'bg-paper-500',
    border: 'border-paper-400',
    animated: true,
  },
  [NodeExecutionStatus.COMPLETED]: {
    bg: 'bg-success-500',
    border: 'border-success-400',
  },
  [NodeExecutionStatus.FAILED]: {
    bg: 'bg-error-500',
    border: 'border-error-400',
  },
  [NodeExecutionStatus.SKIPPED]: {
    bg: 'bg-gray-300',
    border: 'border-gray-200',
  },
};

// ==================== 边状态样式 ====================

export interface EdgeStatusStyle {
  /** 边颜色 */
  stroke: string;
  /** 边宽度 */
  strokeWidth: number;
  /** 是否动画 */
  animated: boolean;
  /** 箭头颜色 */
  markerColor: string;
}

/**
 * 边执行状态样式映射
 */
export const EDGE_STATUS_STYLES: Record<NodeExecutionStatus, EdgeStatusStyle> = {
  [NodeExecutionStatus.PENDING]: {
    stroke: '#9ca3af', // gray-400
    strokeWidth: 2,
    animated: false,
    markerColor: '#9ca3af',
  },
  [NodeExecutionStatus.RUNNING]: {
    stroke: '#f59e0b', // paper-500
    strokeWidth: 2,
    animated: true,
    markerColor: '#f59e0b',
  },
  [NodeExecutionStatus.COMPLETED]: {
    stroke: '#10b981', // success-500
    strokeWidth: 2,
    animated: false,
    markerColor: '#10b981',
  },
  [NodeExecutionStatus.FAILED]: {
    stroke: '#ef4444', // error-500
    strokeWidth: 2,
    animated: false,
    markerColor: '#ef4444',
  },
  [NodeExecutionStatus.SKIPPED]: {
    stroke: '#d1d5db', // gray-300
    strokeWidth: 1.5,
    animated: false,
    markerColor: '#d1d5db',
  },
};

// ==================== 状态显示名称和图标颜色 ====================

export interface StatusDisplayConfig {
  /** 显示名称 */
  label: string;
  /** 图标颜色类 */
  iconColor: string;
  /** 徽章背景色类 */
  badgeBg: string;
  /** 徽章文字色类 */
  badgeText: string;
}

/**
 * 状态显示配置映射
 */
export const STATUS_DISPLAY_CONFIG: Record<NodeExecutionStatus, StatusDisplayConfig> = {
  [NodeExecutionStatus.PENDING]: {
    label: '等待中',
    iconColor: 'text-gray-400',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
  },
  [NodeExecutionStatus.RUNNING]: {
    label: '执行中',
    iconColor: 'text-paper-500',
    badgeBg: 'bg-paper-100',
    badgeText: 'text-paper-800',
  },
  [NodeExecutionStatus.COMPLETED]: {
    label: '已完成',
    iconColor: 'text-success-500',
    badgeBg: 'bg-success-100',
    badgeText: 'text-success-700',
  },
  [NodeExecutionStatus.FAILED]: {
    label: '失败',
    iconColor: 'text-error-500',
    badgeBg: 'bg-error-100',
    badgeText: 'text-error-700',
  },
  [NodeExecutionStatus.SKIPPED]: {
    label: '已跳过',
    iconColor: 'text-gray-400',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-500',
  },
};

// ==================== 工具函数 ====================

/**
 * 合并 Tailwind 类名
 */
function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * 获取节点状态样式类
 *
 * @param status 节点执行状态
 * @param isSelected 是否选中
 * @param isCurrentNode 是否当前执行节点
 * @returns 合并后的 CSS 类名
 */
export function getNodeStatusClasses(
  status: NodeExecutionStatus,
  isSelected: boolean = false,
  isCurrentNode: boolean = false
): string {
  const style = NODE_STATUS_STYLES[status];

  // 基础样式
  const classes: ClassValue[] = [
    'rounded-lg border-2 bg-white shadow-sm',
    'transition-all duration-150',
  ];

  // 选中状态
  if (isSelected) {
    classes.push('ring-2 ring-paper-500 ring-offset-2');
  }

  // 状态样式
  classes.push(style.border);
  if (style.shadow) classes.push(style.shadow);
  if (style.animation) classes.push(style.animation);
  if (style.opacity) classes.push(style.opacity);

  // 当前节点特殊高亮
  if (isCurrentNode && status === NodeExecutionStatus.RUNNING) {
    classes.push('ring-4 ring-paper-200');
  }

  return cn(...classes);
}

/**
 * 获取连接点状态样式类
 *
 * @param status 节点执行状态
 * @returns 合并后的 CSS 类名
 */
export function getHandleStatusClasses(status: NodeExecutionStatus): string {
  const style = HANDLE_STATUS_STYLES[status];

  const classes: ClassValue[] = [
    'w-3 h-3 rounded-full border-2',
    style.bg,
    style.border,
  ];

  if (style.animated) {
    classes.push('animate-pulse');
  }

  return cn(...classes);
}

/**
 * 获取边样式对象
 *
 * @param status 边执行状态
 * @returns 边样式对象
 */
export function getEdgeStatusStyle(status: NodeExecutionStatus): React.CSSProperties {
  const style = EDGE_STATUS_STYLES[status];

  return {
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  };
}

/**
 * 获取状态徽章样式
 *
 * @param status 节点执行状态
 * @returns 合并后的 CSS 类名
 */
export function getStatusBadgeClasses(status: NodeExecutionStatus): string {
  const config = STATUS_DISPLAY_CONFIG[status];

  return cn(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
    config.badgeBg,
    config.badgeText
  );
}

/**
 * 获取状态图标样式
 *
 * @param status 节点执行状态
 * @returns 合并后的 CSS 类名
 */
export function getStatusIconClasses(status: NodeExecutionStatus): string {
  const config = STATUS_DISPLAY_CONFIG[status];

  return cn(
    'w-4 h-4',
    config.iconColor,
    status === NodeExecutionStatus.RUNNING && 'animate-spin'
  );
}

// ==================== 进度条样式 ====================

/**
 * 进度条颜色配置
 */
export const PROGRESS_COLORS = {
  /** 默认/进行中颜色 */
  running: 'bg-paper-500',
  /** 完成颜色 */
  completed: 'bg-success-500',
  /** 失败颜色 */
  failed: 'bg-error-500',
  /** 暂停颜色 */
  paused: 'bg-warning-500',
};

/**
 * 获取进度条颜色
 *
 * @param state 执行状态
 * @returns 进度条颜色类
 */
export function getProgressColor(state: NodeExecutionStatus | 'paused'): string {
  switch (state) {
    case NodeExecutionStatus.COMPLETED:
      return PROGRESS_COLORS.completed;
    case NodeExecutionStatus.FAILED:
      return PROGRESS_COLORS.failed;
    case 'paused':
      return PROGRESS_COLORS.paused;
    default:
      return PROGRESS_COLORS.running;
  }
}

// ==================== 导出所有配置 ====================

export default {
  NODE_STATUS_STYLES,
  HANDLE_STATUS_STYLES,
  EDGE_STATUS_STYLES,
  STATUS_DISPLAY_CONFIG,
  PROGRESS_COLORS,
  getNodeStatusClasses,
  getHandleStatusClasses,
  getEdgeStatusStyle,
  getStatusBadgeClasses,
  getStatusIconClasses,
  getProgressColor,
};
